import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { useUserCanBroadcast } from 'src/hooks/useUserHasListingPermissions';
import { UK_COUNTRY_CODES } from 'src/utils/constants/constants';
import { getIsInternationalEvent } from 'src/utils/eventWithDataUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Event,
  Listing,
  ListingStatus,
  Marketplace,
  MarketplaceListing,
  Performer,
  Venue,
} from 'src/WebApiController';
import { ListingClient } from 'src/WebApiController';

import { MarketplaceDisplayProps } from './MarketplaceBroadcastSection.utils';
import {
  MarketplaceFailure,
  useGetMarketplaceFailures,
} from './useGetMarketplaceFailures';

export const useClientSideMarketplaceListingStatus = (
  listing: Listing
): [
  Record<Marketplace, ListingStatus>,
  React.Dispatch<React.SetStateAction<Record<Marketplace, ListingStatus>>>,
] => {
  // We need to keep a local state of the listing status so we can use this to reflect the listing toggle and status when user clicks
  // otherwise we have to wait until the status is updated in the server before the UI shows the updates
  // However, mainting this local state in sync with the server is a bit nuanced - do be care of changing this logic
  const [marketplaceListingStatus, setMarketplaceListingStatus] = useState<
    Record<Marketplace, ListingStatus>
  >(
    // Initialized this state to what the current market listing status is
    listing.mkpListings
      ?.filter((ml) => ml.mkp)
      .reduce(
        (ml, cur) => {
          ml[cur.mkp!] = cur.status;
          return ml;
        },
        {} as Record<Marketplace, ListingStatus>
      ) ?? ({} as Record<Marketplace, ListingStatus>)
  );

  return [marketplaceListingStatus, setMarketplaceListingStatus];
};

export const useMarketplaceBroadcastSection = (
  listing: Listing,
  disabled: boolean,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  marketplaceListingStatus: Record<Marketplace, ListingStatus>,
  setMarketplaceListingStatus: React.Dispatch<
    React.SetStateAction<Record<Marketplace, ListingStatus>>
  >,
  rawEvent?: Event | null,
  rawPerformer?: Performer | null,
  rawVenue?: Venue | null
) => {
  const { mkpListings } = listing;
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog, trackError } = useClientLogContext();

  const { event, venue } = useGetEventFullInfo(
    rawEvent,
    rawPerformer,
    rawVenue
  );

  const isUKEvent = Boolean(
    venue?.ctryCode && UK_COUNTRY_CODES.includes(venue.ctryCode.toUpperCase())
  );
  const isInternationalEvent = getIsInternationalEvent(venue?.ctryCode);

  const marketplaceFailures = useGetMarketplaceFailures(listing);

  const marketplaceFailuresByMarketplace = useMemo(
    () =>
      marketplaceFailures.reduce(
        (r, c) => {
          r[c.marketplace] = c;
          return r;
        },
        {} as Record<Marketplace, MarketplaceFailure>
      ),
    [marketplaceFailures]
  );

  // Only when the marketling-listing changed should we try to refresh the local states with it
  useEffect(() => {
    const newStatuses = mkpListings
      ?.filter((ml) => ml.mkp)
      .reduce(
        (ml, cur) => {
          ml[cur.mkp!] = cur.status;
          return ml;
        },
        {} as Record<Marketplace, ListingStatus>
      );

    if (newStatuses && !isEqual(newStatuses, marketplaceListingStatus)) {
      setMarketplaceListingStatus(newStatuses);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mkpListings]); // We only want to run this when mkpListings changed - not the other things

  const onSetMarketplaceListingStatus = useCallback(
    (marketplace: Marketplace, status: ListingStatus) => {
      setMarketplaceListingStatus((prev) => ({
        ...prev,
        [marketplace]: status,
      }));
    },
    [setMarketplaceListingStatus]
  );

  const onListingBroadcastToggle = useCallback(
    (isBroadcasting: boolean, marketPlace: Marketplace) => {
      tryInvokeApi(
        async () => {
          setIsLoading(true);

          if (isBroadcasting) {
            await new ListingClient(
              activeAccountWebClientConfig
            ).createMarketplaceListings(listing.id, [marketPlace]);
          } else {
            await new ListingClient(
              activeAccountWebClientConfig
            ).deleteMarketplaceListings(listing.id, [marketPlace]);
          }
        },
        (error) => {
          showErrorDialog(
            isBroadcasting
              ? 'ListingClient.createMarketplaceListings'
              : 'ListingClient.deleteMarketplaceListings',
            error,
            {
              trackErrorData: {
                listingId: listing.id,
                marketPlaces: [marketPlace],
              },
            }
          );
        },
        () => setIsLoading(false)
      );
    },
    [activeAccountWebClientConfig, listing.id, setIsLoading, showErrorDialog]
  );

  const canBroadcast = useUserCanBroadcast(listing, false);

  const isDisabled = disabled || !canBroadcast;

  const getMarketplaceDisplayProps = useCallback(
    (ml: MarketplaceListing, isViagogo: boolean, i: number) => {
      const mpListingStatus = marketplaceListingStatus[ml.mkp!];

      // When the UnitNetProceeds value is 0, we should prevent broadcasting
      const isDisabledWithPriceCheck =
        isDisabled ||
        (mpListingStatus === ListingStatus.Delisted &&
          listing.listPrice === 0 &&
          (ml.netProcs == null || ml.netProcs === 0));

      return {
        marketplaceListing: ml,
        formIndex: i,
        status: mpListingStatus,
        setListingStatus: onSetMarketplaceListingStatus,
        disabled: isDisabledWithPriceCheck,
        isViagogo,
        isDeleted: listing.isDeleted,
        isUKEvent: ml.mkp === Marketplace.StubHub ? isUKEvent : undefined,
        onListingBroadcastToggle,
        error: ml.mkp && marketplaceFailuresByMarketplace[ml.mkp],
        qcState: listing.qcState,
      } as MarketplaceDisplayProps;
    },
    [
      isDisabled,
      isUKEvent,
      listing.isDeleted,
      listing.listPrice,
      listing.qcState,
      marketplaceFailuresByMarketplace,
      marketplaceListingStatus,
      onListingBroadcastToggle,
      onSetMarketplaceListingStatus,
    ]
  );

  return {
    getMarketplaceDisplayProps,
    event,
    venue,
    isInternationalEvent,
  };
};
